/* ---------------------------------------------------------------------------
   dtc-sticky-buy.js — behaviour for the sticky picks bar.

   The bar arrives when the buy block has left the viewport, leaves before the
   footer, and stays gone for the session once dismissed. On a phone the drawer
   holds picks two and three.

   Adding to cart goes through the theme's own flow, copied from
   main-product.js: POST to routes.cart_add_url + ".js" with the drawer's
   section ids attached, then re-render those sections and open the minicart.
   Every hook into the theme is guarded, and the fallback is the plain form
   post, which always works. The cell is a real <form> for exactly that reason.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  // Glozin puts open-drawer / open-minicart / open-search on <html> while an
  // overlay is up. The bar must not sit on top of any of them.
  var OVERLAY = ['open-drawer', 'open-minicart', 'open-search', 'open-popup', 'open-menu'];

  function overlayOpen() {
    var cl = document.documentElement.classList;
    for (var i = 0; i < OVERLAY.length; i += 1) if (cl.contains(OVERLAY[i])) return true;
    return false;
  }

  function session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      // private mode, or storage blocked. The bar simply does not remember.
    }
    return null;
  }

  class DtcStickyBuy extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode === 'read' ? 'read' : 'buy';
      this.key = this.dataset.key || 'dtc-sticky';
      this.dismissed = session(this.key) === 'off';

      this.inner = this.querySelector('.dtc-sticky__inner');
      this.sheet = this.querySelector('.dtc-sticky__sheet');
      this.more = this.querySelector('[data-more]');
      this.scrim = this.querySelector('[data-scrim]');
      this.cells = Array.prototype.slice.call(this.querySelectorAll('.dtc-sticky__item'));

      this.startEl = this.pick(this.dataset.startAfter);
      this.footEl = this.pick('.footer, footer, [id*="section-footer"]');

      this.here = null;
      this.shown = false;
      this.frame = 0;

      this.onScroll = this.schedule.bind(this);
      this.onKey = this.handleKey.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      document.addEventListener('keydown', this.onKey);

      this.wire();
      this.hidden = false;   // JS is running, so the bar may now show itself
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('keydown', this.onKey);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    pick(selector) {
      if (!selector) return null;
      try {
        var el = document.querySelector(selector);
        return el && !this.contains(el) ? el : null;
      } catch (e) {
        return null;
      }
    }

    wire() {
      var self = this;

      Array.prototype.forEach.call(this.querySelectorAll('[data-close]'), function (btn) {
        btn.addEventListener('click', function () {
          self.dismissed = true;
          session(self.key, 'off');
          self.closeSheet();
          self.update();
        });
      });

      if (this.more && this.sheet) {
        this.more.addEventListener('click', function () {
          if (self.sheet.hidden) self.openSheet(); else self.closeSheet();
        });
      }
      if (this.scrim) this.scrim.addEventListener('click', function () { self.closeSheet(); });

      // A jump closes the drawer behind it, or the reader lands on a section
      // hidden under an open sheet.
      Array.prototype.forEach.call(this.querySelectorAll('[data-go]'), function (link) {
        link.addEventListener('click', function () { self.closeSheet(); });
      });

      Array.prototype.forEach.call(this.querySelectorAll('form[data-add]'), function (form) {
        form.addEventListener('submit', function (event) { self.add(event, form); });
      });
    }

    openSheet() {
      if (!this.sheet) return;
      this.sheet.hidden = false;
      if (this.scrim) this.scrim.hidden = false;
      if (this.more) this.more.setAttribute('aria-expanded', 'true');
      this.classList.add('is-sheet');
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.hidden = true;
      if (this.scrim) this.scrim.hidden = true;
      if (this.more) this.more.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-sheet');
    }

    handleKey(event) {
      if (event.key === 'Escape' && this.sheet && !this.sheet.hidden) {
        this.closeSheet();
        if (this.more) this.more.focus();
      }
    }

    schedule() {
      var self = this;
      if (this.frame) return;
      this.frame = requestAnimationFrame(function () {
        self.frame = 0;
        self.update();
      });
    }

    update() {
      var h = window.innerHeight;
      var past = this.startEl
        ? this.startEl.getBoundingClientRect().bottom < 8
        : window.pageYOffset > 600;
      var footNear = this.footEl
        ? this.footEl.getBoundingClientRect().top < h - 80
        : false;

      var show = past && !footNear && !this.dismissed && !overlayOpen();
      if (show !== this.shown) {
        this.shown = show;
        this.classList.toggle('is-shown', show);
        if (!show) this.closeSheet();
      }
      if (this.mode === 'read') this.spy(h);
    }

    // Which breakdown the reader is inside, so the bar doubles as a position
    // marker through the ranked answer.
    spy(h) {
      var here = null;
      for (var i = 0; i < this.cells.length; i += 1) {
        var anchor = this.cells[i].dataset.anchor;
        if (!anchor) continue;
        var target = document.getElementById(anchor);
        if (!target) continue;
        var r = target.getBoundingClientRect();
        if (r.top < h * 0.45 && r.bottom > 90) here = anchor;
      }
      if (here === this.here) return;
      this.here = here;
      this.cells.forEach(function (cell) {
        cell.classList.toggle('is-here', !!here && cell.dataset.anchor === here);
      });
    }

    add(event, form) {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var url = window.routes && window.routes.cart_add_url;

      // No theme helpers means no AJAX. Let the form post the old way.
      if (typeof fetchConfig !== 'function' || !url) return;
      event.preventDefault();

      var self = this;
      var button = form.querySelector('button[type="submit"]');
      var label = button && button.querySelector('.dtc-sticky__golabel');
      var config = fetchConfig('json');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      var data = new FormData(form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        data.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        data.append('sections_url', window.location.pathname);
      }
      config.body = data;
      if (button) button.classList.add('loading');

      fetch(url + '.js', config)
        .then(function (response) { return response.json(); })
        .then(function (state) {
          if (state.status || state.message) {
            if (typeof handleErrorMessagePopup === 'function') {
              handleErrorMessagePopup(state.description || state.message);
            }
            return;
          }
          self.refreshCount();
          self.paint(cart, state);
          if (label) {
            label.textContent = 'Added';
            button.classList.add('is-done');
            setTimeout(function () {
              label.textContent = 'Add';
              button.classList.remove('is-done');
            }, 2200);
          }
          self.closeSheet();
        })
        .catch(function () {
          // The add may or may not have landed. A reload through the normal
          // post is the only honest recovery.
          form.submit();
        })
        .finally(function () {
          if (button) button.classList.remove('loading');
        });
    }

    // Re-render the minicart and open it, the way the product page does. Every
    // step is guarded: the item is already in the cart by this point, so a
    // markup change in the theme must not surface as a failed add.
    paint(cart, state) {
      if (!cart || !state.sections || typeof cart.getSectionsToRender !== 'function') return;
      try {
        cart.getSectionsToRender().forEach(function (section) {
          var target = document.getElementById(section.id);
          var markup = state.sections[section.id];
          if (!target || !markup) return;
          var parsed = new DOMParser().parseFromString(markup, 'text/html');
          var form = parsed.querySelector('#minicart-form');
          if (form) target.innerHTML = form.innerHTML;
        });
        if (typeof cart.cartAction === 'function') cart.cartAction();
        if (typeof showMinicartAddSuccessMessage === 'function') showMinicartAddSuccessMessage(cart);
        if (typeof cart.open === 'function') cart.open();
      } catch (e) {
        // leave the drawer alone; the count is still correct
      }
    }

    refreshCount() {
      fetch('/cart.json')
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          if (cart.item_count === undefined) return;
          Array.prototype.forEach.call(document.querySelectorAll('.cart-count'), function (el) {
            if (el.classList.contains('cart-count-drawer')) {
              el.innerHTML = '(' + cart.item_count + ')';
            } else {
              el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
            }
          });
          var total = document.querySelector('header-total-price');
          if (total && typeof total.updateTotal === 'function') total.updateTotal(cart);
          var ship = document.querySelector('free-ship-progress-bar');
          if (ship && typeof ship.init === 'function') ship.init(cart.items_subtotal_price);
        })
        .catch(function () {});
    }
  };

  if (!customElements.get('dtc-sticky-buy')) {
    customElements.define('dtc-sticky-buy', DtcStickyBuy);
  }
})();
